import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Grid, Type } from '../../../app/StyleGuide';
import { BreadcrumbsSubMenu, Crumb, MobileCrumb } from '../../../components/Breadcrumbs';
import { MediumOutlineButton } from '../../../components/Buttons';
import { CurriculumBrandBanner } from '../../../components/CurriculumBrandBanner';
import { Container } from '../../../components/Layout';
import { Tab, TabContainer } from '../../../components/Tabs';
import windowService from '../../../services/windowService';
import { logger } from '../../../utils/logger';
import { AgeCategory, BrandCode, curriculumConfig } from '../../curriculum';
import { getAdvancePlanningMaterials } from '../api/getAdvancePlanningMaterials';
import { AnnualSubscriptionUpgradeButton } from './AnnualSubscriptionUpgradeButton';

type Props = {
  crumbs: Crumb[];
  brandCode: BrandCode;
  ageCategory: AgeCategory;
  curriculumId: string;
  orgId: string | undefined;
  activeTab: string;
};

const Header = styled.header`
  display: flex;
  flex-direction: column;
  margin: ${Grid._4} auto;
  @media screen and (min-width: ${Breakpoints.screen_lg}) {
    margin: ${Grid._5} auto;
  }
  h1 {
    font-weight: ${Type.Weight.bold};
    font-size: ${Type.Scale._6};
    text-transform: capitalize;
    color: ${Type.Color.dark};
    margin: ${Grid._3} 0 0;
    order: 1;
  }
`;

const ContentContainer = styled(Container)`
  position: relative;

  ${MediumOutlineButton} {
    position: absolute;
    top: 0;
    right: ${Grid._4};
  }
`;

const useShowAdvancePlanningTab = (curriculumId: string) => {
  const [showAdvancePlanningTab, setShowAdvancePlanningTab] = useState(false);

  useEffect(() => {
    if (!curriculumId) return;

    getAdvancePlanningMaterials(curriculumId)
      .then((volumes: any[]) => setShowAdvancePlanningTab(volumes.length > 0))
      .catch((e: Error) => logger.error(e.message));
  }, [curriculumId]);

  return showAdvancePlanningTab;
};

type TabItem = {
  key: 'overview' | 'people' | 'schedule' | 'advance-planning';
  name: string;
  icon: string;
  route: (brandCode: string, ageCategory: string, curriculumId: string) => string;
};

const tabItems: TabItem[] = [
  {
    key: 'overview',
    name: 'Overview',
    icon: 'fas fa-home',
    route: (brandCode: string, ageCategory: string, curriculumId: string) =>
      `/manage-bible-study/overview/${brandCode}/${ageCategory}/${curriculumId}`,
  },
  {
    key: 'people',
    name: 'People',
    icon: 'fas fa-user-friends',
    route: (brandCode: string, ageCategory: string, curriculumId: string) =>
      `/manage-bible-study/people/${brandCode}/${ageCategory}/${curriculumId}`,
  },
  {
    key: 'schedule',
    name: 'Schedule',
    icon: 'far fa-calendar-alt',
    route: (brandCode: string, ageCategory: string, curriculumId: string) =>
      `/manage-bible-study/schedule/${brandCode}/${ageCategory}/${curriculumId}`,
  },
  {
    key: 'advance-planning',
    name: 'Advance Planning',
    icon: 'far fa-folder-open',
    route: (brandCode: string, ageCategory: string, curriculumId: string) =>
      `/manage-bible-study/advance-planning/${brandCode}/${ageCategory}/${curriculumId}`,
  },
];

export const HeaderNavigation = ({ crumbs, brandCode, ageCategory, curriculumId, orgId, activeTab }: Props) => {
  const showAdvancePlanningTab = useShowAdvancePlanningTab(curriculumId);
  const isOverviewEnabled = curriculumConfig.overviewEnabledCurriculum.includes(brandCode);

  const isTabHidden = (tabKey: string) => {
    if (tabKey === 'advance-planning' && !showAdvancePlanningTab) return true;
    if (tabKey === 'overview' && !isOverviewEnabled) return true;
    return false;
  };

  return (
    <>
      <div className="hidden-xs hidden-sm">
        <BreadcrumbsSubMenu crumbs={crumbs} />
      </div>
      <CurriculumBrandBanner brandCode={brandCode} ageCategory={ageCategory} />
      <ContentContainer>
        <Header>
          <h1>{ageCategory}</h1>
          <div className="visible-xs-block visible-sm-block">
            <MobileCrumb crumbs={crumbs} />
          </div>
        </Header>
        <TabContainer>
          {tabItems.map(
            tab =>
              !isTabHidden(tab.key) && (
                <Tab
                  key={tab.key}
                  active={activeTab === tab.key}
                  iconClass={tab.icon}
                  name={tab.name}
                  onClick={() => windowService.redirectTo(tab.route(brandCode, ageCategory, curriculumId))}
                />
              )
          )}
        </TabContainer>
        <AnnualSubscriptionUpgradeButton curriculumId={curriculumId} orgId={orgId} />
      </ContentContainer>
    </>
  );
};
